import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */
const PRODUCT_TITLE_RESIZE_FALLBACK_KEY = '__mmProductTitleResizeFallback';

/**
 * Shared fallback resize manager for browsers without ResizeObserver support.
 * Keeps a single window listener and fans out recalculation to active instances.
 */
function getResizeFallbackState() {
  /** @type {Window & { [PRODUCT_TITLE_RESIZE_FALLBACK_KEY]?: {
   * instances: Set<ProductTitle>;
   * listenerAttached: boolean;
   * resizeQueued: boolean;
   * onResize: () => void;
   * } }} */
  const globalWindow = window;

  if (!globalWindow[PRODUCT_TITLE_RESIZE_FALLBACK_KEY]) {
    const state = {
      instances: new Set(),
      listenerAttached: false,
      resizeQueued: false,
      onResize: () => {
        if (state.resizeQueued) return;
        state.resizeQueued = true;
        requestAnimationFrame(() => {
          state.resizeQueued = false;
          state.instances.forEach((instance) => {
            if (instance.isConnected) instance.recalculate();
          });
        });
      },
    };

    globalWindow[PRODUCT_TITLE_RESIZE_FALLBACK_KEY] = state;
  }

  return globalWindow[PRODUCT_TITLE_RESIZE_FALLBACK_KEY];
}

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.#calculateTruncation();
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      const fallbackState = getResizeFallbackState();
      fallbackState.instances.add(this);
      if (!fallbackState.listenerAttached) {
        window.addEventListener('resize', fallbackState.onResize);
        fallbackState.listenerAttached = true;
      }
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  recalculate() {
    this.#calculateTruncation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    if (!('ResizeObserver' in window)) {
      const fallbackState = getResizeFallbackState();
      fallbackState.instances.delete(this);
      if (fallbackState.listenerAttached && fallbackState.instances.size === 0) {
        window.removeEventListener('resize', fallbackState.onResize);
        fallbackState.listenerAttached = false;
        fallbackState.resizeQueued = false;
      }
    }
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
